import type { ChangeLogOperation } from "../../../clients/graphqlTypes.js";
import type {
  ChangeLogActor,
  ChangeLogEntity,
  ChangeLogEntry,
} from "./changeLogTypes.js";

export type ChangeLogActionGroup = {
  /**
   * Stable identity of this rendered row — `correlationId` qualified by the run's
   * first entry id. NOT the same thing as `correlationId`: one action can surface
   * as more than one adjacency run (see `groupEntriesByCorrelation`), and the grid
   * needs a key that is unique per *row*.
   */
  id: string;
  correlationId: string;
  occurredAt: string;
  actor: ChangeLogActor;
  entries: ChangeLogEntry[];
};

/**
 * Group the flat, newest-first edge list into one row per **adjacency run** of a
 * `correlationId`, in a single linear pass that preserves the newest-first ordering
 * (design D2).
 *
 * Normally one action is one run. But grouping is adjacency-based over the keyset
 * order `(occurredAt, id)`, and Layer 3 does NOT guarantee an action's entries are
 * contiguous: capture seams each mint their own `occurredAt`, so a multi-seam action
 * spans several timestamps, and a concurrent action's rows can interleave into that
 * span — the change-log-service spec documents this and accepts the action surfacing
 * as two rows. Accumulated infinite-scroll pages make it likelier still, since the
 * runs can land in different pages.
 *
 * Hence `id` is per-run rather than the bare `correlationId`: two runs of one action
 * would otherwise collide on the grid's row id, and MUI DataGrid requires row ids to
 * be unique — it drops the duplicate, silently losing the second run's entries.
 */
export function groupEntriesByCorrelation(
  entries: ReadonlyArray<ChangeLogEntry>
): ChangeLogActionGroup[] {
  const groups: ChangeLogActionGroup[] = [];
  for (const entry of entries) {
    const last = groups.at(-1);
    if (last && last.correlationId === entry.correlationId) {
      last.entries.push(entry);
    } else {
      groups.push({
        id: `${entry.correlationId}:${entry.id}`,
        correlationId: entry.correlationId,
        occurredAt: entry.occurredAt,
        actor: entry.actor,
        entries: [entry],
      });
    }
  }
  return groups;
}

/**
 * The distinct entities an action touched, in first-seen order — feeds the modal's
 * per-entity rows and the content-derived summary. Deduped by `entityId` (satellite
 * field changes share the item's id).
 */
export function getActionGroupEntities(
  group: ChangeLogActionGroup
): ChangeLogEntity[] {
  const seen = new Set<string>();
  const entities: ChangeLogEntity[] = [];
  for (const entry of group.entries) {
    if (!seen.has(entry.entity.entityId)) {
      seen.add(entry.entity.entityId);
      entities.push(entry.entity);
    }
  }
  return entities;
}

/**
 * The distinct CRUD operations an action carries, in first-seen order (pure events —
 * null operation — contribute nothing). Feeds the content-derived summary: one shared
 * lifecycle operation makes a "«entity» created/deleted" heading, otherwise a change count.
 */
export function getActionGroupOperations(
  group: ChangeLogActionGroup
): ChangeLogOperation[] {
  const seen = new Set<ChangeLogOperation>();
  const operations: ChangeLogOperation[] = [];
  for (const entry of group.entries) {
    if (entry.operation != null && !seen.has(entry.operation)) {
      seen.add(entry.operation);
      operations.push(entry.operation);
    }
  }
  return operations;
}

/** Number of recorded entries in the action (field rows + any lifecycle row). */
export function getActionChangeCount(group: ChangeLogActionGroup): number {
  return group.entries.length;
}

/**
 * The entity types that are an aggregate ROOT rather than one of its children: the
 * document the UI deep-links (`DOCUMENT`) plus the document-shaped aggregates that
 * carry their own root type (`INVOICE` / `ORDER`, RF-279 D6) and the requirement
 * aggregate's own root (`REQUIREMENT`, RF-262 design D1). A multi-entity action is
 * summarised by its root, so this set has to name every root — keying on `DOCUMENT`
 * alone would summarise an invoice, order or requirement action by the coarse aggregate
 * label and lose the root's own name. `PROJECT` is deliberately absent: a project
 * action's fallback IS the "Project" aggregate label, so it needs no root lookup.
 */
const ROOT_ENTITY_TYPES: readonly string[] = [
  "DOCUMENT",
  "INVOICE",
  "ORDER",
  "REQUIREMENT",
];

/** Whether an entity type is an aggregate root (see {@link ROOT_ENTITY_TYPES}). */
export function isRootEntityType(entityType: string): boolean {
  return ROOT_ENTITY_TYPES.includes(entityType);
}

/** The satellite namespace whose fields are the pricing engine's derived output. */
const PRICING_FIELD_PREFIX = "pricing.";

/** A `pricing.*` field is a recomputation by-product, never a direct user input. */
function isPricingField(field: string | null | undefined): boolean {
  return field != null && field.startsWith(PRICING_FIELD_PREFIX);
}

/** A surviving ("primary") entity: its entries and its non-`pricing.*` changed fields. */
export type PrimaryActionEntity = {
  entity: ChangeLogEntity;
  entries: ChangeLogEntry[];
  /** The entity's non-`pricing.*` `CHANGED` field codes, first-seen and deduped. */
  changedFields: string[];
};

/** The split of an action's touched entities into the real edits and the by-products. */
export type ActionGroupClassification = {
  primary: PrimaryActionEntity[];
  cascade: ChangeLogEntity[];
};

/**
 * Split an action group's entities into **primary** (the real edits) and **cascade**
 * (recomputation by-products), so the list row can describe what the user actually did
 * instead of the whole pricing cascade (design D2).
 *
 * A **cascade** entity was ONLY `CHANGED` (no `CREATED`/`DELETED`) AND every one of its
 * `CHANGED` entries carries a non-null field in the derived `pricing.*` namespace — the
 * signature of an ancestor whose pricing recomputed because a descendant's price changed.
 * A `CHANGED` entry with a null field or a non-pricing field keeps the entity primary.
 *
 * **Never-suppress-to-empty guard:** a pure-pricing action (every entity a cascade, e.g. a
 * direct `pricePerUnit` edit in netPrice mode) keeps them ALL as primary — the pricing IS
 * the edit. Each primary's own `pricing.*` fields are dropped from `changedFields`; they
 * are the derived echo of the real edit, not the edit.
 */
export function classifyActionGroupEntities(
  group: ChangeLogActionGroup
): ActionGroupClassification {
  const classified = getActionGroupEntities(group).map(entity => {
    const entries = group.entries.filter(
      e => e.entity.entityId === entity.entityId
    );
    const hasLifecycle = entries.some(
      e => e.operation === "CREATED" || e.operation === "DELETED"
    );
    const changedEntries = entries.filter(e => e.operation === "CHANGED");
    const isCascade =
      !hasLifecycle &&
      changedEntries.length > 0 &&
      changedEntries.every(e => isPricingField(e.field));

    const seen = new Set<string>();
    const changedFields: string[] = [];
    for (const e of changedEntries) {
      if (e.field != null && !isPricingField(e.field) && !seen.has(e.field)) {
        seen.add(e.field);
        changedFields.push(e.field);
      }
    }
    return { entity, entries, changedFields, isCascade };
  });

  const allCascade =
    classified.length > 0 && classified.every(c => c.isCascade);

  const primary: PrimaryActionEntity[] = [];
  const cascade: ChangeLogEntity[] = [];
  for (const c of classified) {
    if (c.isCascade && !allCascade) {
      cascade.push(c.entity);
    } else {
      primary.push({
        entity: c.entity,
        entries: c.entries,
        changedFields: c.changedFields,
      });
    }
  }
  return { primary, cascade };
}

export type FieldValueDisplay =
  | { kind: "hidden" }
  | { kind: "empty" }
  | { kind: "value"; value: unknown };

/**
 * Map a tri-state `ChangeLogFieldValue` to a display state (design D1/D7), read
 * independently per side. `recorded: false` → hidden (masked / oversize / not
 * recorded); `recorded: true` with a recorded-empty value → empty; otherwise the value.
 * A null wrapper (CREATED / DELETED lifecycle entries) is treated as hidden.
 *
 * "Recorded-empty" (design D1) is `null`/`undefined`, an empty or whitespace-only
 * string, or an empty array — decided display-side because the log is append-only and
 * already contains `""` values (many captured text columns default `''::text`), so a
 * capture-side normalization could never cover existing rows. Whitespace-only counts as
 * empty for consistency with the backend's own name-hygiene rule. The empty array
 * matters for `role_ids`, the only array-valued captured code: a cleared role list is
 * "empty", not "unresolvable" (the reference-miss `—` D6 introduces elsewhere).
 */
export function describeFieldValue(
  fieldValue: { recorded: boolean; value?: unknown } | null | undefined
): FieldValueDisplay {
  if (!fieldValue || !fieldValue.recorded) return { kind: "hidden" };
  const { value } = fieldValue;
  if (value === null || value === undefined) return { kind: "empty" };
  if (typeof value === "string" && value.trim() === "") {
    return { kind: "empty" };
  }
  if (Array.isArray(value) && value.length === 0) {
    return { kind: "empty" };
  }
  return { kind: "value", value };
}
